import { Injectable, inject } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { sha256File } from '../helpers/file-hash';
import { AppHttpService } from './app-http.service';
import {
  AsBuiltStatus,
  DrawingAsBuiltListParams,
  DrawingAsBuiltPage,
  DrawingAsBuiltRow,
  DrawingCarryDecision,
  DrawingCarryForward,
  DrawingExportJob,
  DrawingExportKind,
  DrawingIssueSet,
  DrawingLinkedPage,
  DrawingLinkedRecord,
  DrawingPlacement,
  DrawingPlaceRequest,
  DrawingPlaceResult,
  DrawingRevisionDetail,
  DrawingSetRevision,
  DrawingSheet,
  DrawingSheetDetail,
  DrawingSheetListParams,
} from '../models/drawing-sheet';

const FALLBACK_COPY = 'Something went wrong. Try again.';
/** The API's MAX_DRAWING_SET_UPLOAD_BYTES (300 MB): the sheet worker holds the whole PDF in memory */
export const DRAWING_SET_MAX_BYTES = 300 * 1024 * 1024;
/** The PUT to S3 got no answer (offline, aborted, CORS): the client's own code, so it reads as an interrupted upload */
export const DRAWING_UPLOAD_INTERRUPTED = 'DRAWING_UPLOAD_INTERRUPTED';
export const UPLOAD_INTERRUPTED_COPY = 'Upload interrupted. Check your connection and try again.';
/** No response at all (HTTP status 0): the app's own wording, for a save and for anything else */
export const OFFLINE_SAVE_COPY = "Couldn't save. Check your connection and try again.";
export const OFFLINE_COPY = 'Unable to connect. Check your connection and try again.';

/** A page of the sheets list (GET …/drawings/sheets) */
export interface DrawingSheetListPage {
  data: DrawingSheet[];
  totalRecords: number;
  asBuiltOpen?: number;
  asBuiltTotal?: number;
  disciplines?: string[];
  /** W57: facet counts for the register's Group by ('' = no discipline; `other` takes sheets with no type) */
  disciplineCounts?: Record<string, number>;
  sheetTypeCounts?: Record<string, number>;
}

/** The presigned PUT an upload intent grants: send exactly these headers (the browser adds content-length) */
export interface DrawingUploadGrant {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresIn: number;
}

/** POST …/sets/upload-intent: a new or resumed set with its grant (201), or a draft of the same file reused (200) */
export interface DrawingUploadIntent {
  set: DrawingIssueSet;
  upload: DrawingUploadGrant | null;
  reused: boolean;
}

export interface DrawingUploadIntentRequest {
  filename: string;
  bytes: number;
  sha256: string;
  name?: string;
  issued_at?: string | null;
  sheet_uuid?: string;
}

/** Bytes so far of a step that has a size: the file read for its hash, or the PUT */
export interface DrawingTransferProgress {
  loaded: number;
  total: number;
}

/** What a direct upload is doing: hashing, uploading, finalizing, then done with the set (reused: no upload ran) */
export type DrawingUploadEvent =
  | ({ stage: 'hashing' } & DrawingTransferProgress)
  | ({ stage: 'uploading' } & DrawingTransferProgress)
  | { stage: 'finalizing' }
  | { stage: 'done'; set: DrawingIssueSet; reused: boolean };

/** W72: what a single-sheet download carries: the bare sheet, shared plus own redlines, or as-built marks (not rejected) */
export type DrawingDownloadMode = 'clean' | 'redlines' | 'as_built';

/** The file name in a Content-Disposition header, preferring the RFC 5987 `filename*` form */
export function attachmentFileName(header: string | null | undefined): string | null {
  if (!header) {
    return null;
  }
  const extended = header.match(/filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[2].trim().replace(/^"|"$/g, '')) || null;
    } catch {
      // fall through to the plain form
    }
  }
  const plain = header.match(/filename\s*=\s*("([^"]*)"|[^;]*)/i);
  const name = (plain?.[2] ?? plain?.[1] ?? '').trim();
  return name || null;
}

function isCounts(value: unknown): value is Record<string, number> {
  return !!value && typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(v => typeof v === 'number');
}

/** W59: shared markup belongs to its author and the project's admins */
export const MARKUP_NOT_OWNER_COPY = "You can't edit this markup";

/**
 * Review M1: why a markup is locked (can_edit false), in the API's terms (option B): an as-built mark past open is
 * nobody's to edit; an open one is its author's; anything else gets the plain reason.
 */
export function markupLockCopy(row: { kind?: string | null; review_status?: string | null } | null | undefined): string {
  if (row?.kind === 'as_built') {
    return row.review_status && row.review_status !== 'open'
      ? "Reviewed as-built marks can't be edited"
      : 'Only its author can edit an open as-built mark';
  }
  return MARKUP_NOT_OWNER_COPY;
}

const COPY: Record<string, string> = {
  // Spec section 10
  DRAWING_PDF_UNREADABLE: 'This PDF could not be read. Check that it opens normally and is not password protected.',
  DRAWING_NUMBER_DUPLICATE_IN_SET: 'A sheet number appears twice in this set.',
  DRAWING_LABEL_EXISTS: 'That revision label already exists for this sheet. Change the label.',
  DRAWING_SET_NOT_READY: 'Wait for the set to finish inspecting before publishing.',
  DRAWING_SHEET_LINKED: 'This sheet has linked records. Withdraw it instead.',
  MARKUP_VERSION_CONFLICT: 'Someone else changed this redline. Reload to see their version.',
  // Drawings
  DRAWING_SET_PUBLISHED: 'This set is already published.',
  DRAWING_SET_ALL_SKIPPED: 'Every page is skipped. Include at least one page to publish.',
  MARKUP_NOT_OWNER: MARKUP_NOT_OWNER_COPY,
  DRAWING_NUMBER_EXISTS: 'That sheet number is already on this project.',
  DRAWING_PUBLISH_CONFLICT: 'Someone else published at the same time. Reload and try again.',
  DRAWING_SHEET_NOT_FOUND: 'This sheet no longer exists.',
  DRAWING_SET_NOT_FOUND: 'This set no longer exists.',
  DRAWING_REVISION_NOT_FOUND: 'This revision no longer exists.',
  DRAWING_MARKUP_NOT_FOUND: 'That redline no longer exists.',
  DRAWING_ALIGNMENT_NOT_FOUND: 'That alignment was not found on this project.',
  DRAWING_NOTHING_TO_CARRY: 'There are no redlines to carry forward.',
  DRAWING_SHEET_POINT_PAGE: 'The marked point is not on this revision page.',
  DRAWING_REVISION_NOT_LINKABLE: 'Only current or superseded revisions can be linked.',
  DRAWING_LAYER_SOURCE_NOT_PDF: 'This map layer is not a PDF.',
  DRAWING_LAYER_ALREADY_REGISTERED: 'This map layer is already registered as a drawing.',
  DRAWING_LAYER_NOT_PREPARED: 'This map layer is not ready yet. Try again shortly.',
  DRAWING_SOURCE_ORIGINAL_MISSING: 'The original file for this layer is missing. Re-upload it before registering it as a drawing.',
  // Markup
  MARKUP_EXISTS: 'A redline already exists here. Reload to see it.',
  // R51: the client id belongs to a deleted mark, or to one on another page; the editor drops it, so Save makes a new one
  MARKUP_CLIENT_UUID_REUSED: 'This as-built mark could not be matched to its earlier save. Save again to record it as a new mark.',
  MARKUP_VISIBILITY_IMMUTABLE: 'A redline cannot change between Shared and Private.',
  // As-built (spec §15)
  MARKUP_AS_BUILT_LOCKED: 'This as-built mark has been reviewed and can no longer be changed.',
  MARKUP_REVIEW_TRANSITION_INVALID: 'Someone changed this mark\'s status meanwhile. Check its new status and try again.',
  MARKUP_KIND_IMMUTABLE: 'A field note cannot become an as-built mark, or the other way round.',
  MARKUP_AS_BUILT_SHARED_ONLY: 'As-built marks are always shared.',
  MARKUP_AS_BUILT_DRAWING_ONLY: 'As-built marks can only be recorded on drawing sheets.',
  MARKUP_NOT_AS_BUILT: 'Only as-built marks can be reviewed.',
  MARKUP_INCORPORATED_REVISION_INVALID: 'Pick a later revision of this sheet.',
  MARKUP_REVIEW_BULK_INVALID: 'Some marks could not change. Nothing was changed.',
  // Placement
  PLACEMENT_TOO_FEW_POINTS: 'This page has no embedded georeferencing. Add at least 3 control points.',
  PLACEMENT_REGION_EMPTY: 'Choose a larger area of the sheet to place.',
  PLACEMENT_EXISTING_SOURCE_POINTS: 'This layer already has its own georeferencing. Remove the control points.',
  PLACEMENT_CRS_UNKNOWN: 'Set the project location before placing drawings on the map.',
  PLACEMENT_PAGE_UNREADABLE: 'This page could not be read for placement.',
  PLACEMENT_REVISION_NOT_ISSUED: 'Only issued revisions can be placed on the map.',
  PLACEMENT_LAYER_NOT_FLAGGED: 'That layer is not waiting for a new placement.',
  PLACEMENT_LAYER_AMBIGUOUS: 'More than one layer is waiting for placement. Pick one.',
  PLACEMENT_SOURCE_MISMATCH: 'This layer was not registered from this drawing revision.',
  PLACEMENT_CHECK_FAILED: 'These points could not be fitted. Spread the control points out and try again.',
  PLACEMENT_IN_PROGRESS: 'This sheet is already being placed. Wait for it to finish.',
  PLACEMENT_PAGE_TOO_LARGE: 'This page is too large to place on the map.',
  PLACEMENT_NO_ALIGNMENT: 'This project has no alignment. Add one before placing a drawing by stations.',
  PLACEMENT_ALIGNMENT_NOT_FOUND: 'That alignment is no longer on this project. Choose another.',
  PLACEMENT_ALIGNMENT_AMBIGUOUS: 'This project has more than one alignment. Choose the one the stations are on.',
  PLACEMENT_TOO_FEW_STATIONS: 'Place at least two stations on the sheet.',
  PLACEMENT_STATION_OUT_OF_RANGE: 'A station is not on the alignment. Check the station numbers.',
  PLACEMENT_STATIONS_COINCIDE: 'Place the stations further apart on the sheet.',
  PLACEMENT_NOT_IN_REVIEW: 'This placement is no longer waiting for review. Its current state is shown.',
  // Remove from map (api 3692cccc)
  PLACEMENT_NOT_PUBLISHED: 'This sheet is no longer on the map. Its current state is shown.',
  MAP_CATALOG_CONFLICT: 'The map changed. Reload and try again.',
  MAP_SOURCE_NOT_FOUND: 'That map layer no longer exists.',
  // Direct upload
  DRAWING_FILE_TOO_LARGE: 'This PDF is larger than 300 MB. Split it into smaller sets and upload each one.',
  DRAWING_UPLOAD_MISSING: 'The file did not finish uploading. Try again.',
  DRAWING_UPLOAD_MISMATCH: 'The uploaded file did not match the one you picked. Try again.',
  DRAWING_UPLOAD_FAILED: 'The file could not be uploaded. Try again.',
  DRAWING_FILE_UNREADABLE: 'This file could not be read. Pick it again.',
  [DRAWING_UPLOAD_INTERRUPTED]: UPLOAD_INTERRUPTED_COPY,
  // Export
  EXPORT_IN_PROGRESS: 'Another export is already running. Try again in a few minutes.',
  EXPORT_NOT_FOUND: 'That export no longer exists.',
};

const SERVER_MESSAGE_CODES = new Set([
  'DRAWING_LABEL_EXISTS',
  'DRAWING_SET_NOT_READY',
  'DRAWING_NUMBER_DUPLICATE_IN_SET',
  'DRAWING_NUMBER_EXISTS',
  'DRAWING_PUBLISH_CONFLICT',
  // Two causes under one code (m-D): apart on the sheet, or the same point on the ground
  'PLACEMENT_STATIONS_COINCIDE',
  // Names the station and the alignment
  'PLACEMENT_STATION_OUT_OF_RANGE',
]);

/** The browser save (file-saver), behind one seam so specs stub it and never write a file into Downloads */
export const drawingFileSaver = {
  save: (blob: Blob, name: string): void => saveAs(blob, name),
};

@Injectable({ providedIn: 'root' })
export class ProjectDrawingsService {
  private http = inject(AppHttpService);

  private base(projectId: string): string {
    return `/v1/project/${projectId}/drawings`;
  }

  /**
   * W72: streams one sheet revision's PDF (clean, with redlines, or with as-built marks) and hands it to the browser
   * under the Content-Disposition filename. A failed blob request carries its JSON error body as a Blob, so it is
   * parsed back into `{ status, error: { code, message } }` for the usual failure copy.
   */
  downloadRevision(projectId: string, revisionUuid: string, mode: DrawingDownloadMode, fallbackName: string): Observable<string> {
    return this.http.getService(`${this.base(projectId)}/revisions/${revisionUuid}/download`, { mode }, { responseType: 'blob', observe: 'response' }).pipe(
      map((res: HttpResponse<Blob>) => {
        const name = attachmentFileName(res.headers?.get('Content-Disposition')) || fallbackName;
        drawingFileSaver.save(res.body, name);
        return name;
      }),
      catchError((e: { status?: number; error?: unknown }) => {
        if (!(e?.error instanceof Blob)) {
          return throwError(() => e);
        }
        return from(e.error.text()).pipe(
          map(text => { try { return JSON.parse(text); } catch { return null; } }),
          mergeMap(body => throwError(() => ({ status: e.status, error: body ?? undefined }))),
        );
      }),
    );
  }


  private unwrap<T>() {
    return map((resp: { data?: T } | null | undefined): T => resp?.data as T);
  }

  /**
   * `asBuiltTotal` (every as-built mark of the project, any revision or status, never narrowed by these filters) comes
   * for every reader; `asBuiltOpen` (the open ones) only for publishers.
   */
  /** The sheets list. W68: the web sends no position (Sheets here, X-Position, is mobile's; the API keeps it) */
  listSheets(projectId: string, qp: DrawingSheetListParams = {}): Observable<DrawingSheetListPage> {
    return this.http.getService(`${this.base(projectId)}/sheets`, qp).pipe(
      map((resp: { data?: DrawingSheet[]; meta?: { totalRecords?: number; asBuiltOpen?: number; asBuiltTotal?: number; disciplines?: string[];
        discipline_counts?: Record<string, number>; sheet_type_counts?: Record<string, number> } }) => ({
        data: resp?.data ?? [],
        totalRecords: resp?.meta?.totalRecords ?? 0,
        ...(typeof resp?.meta?.asBuiltOpen === 'number' ? { asBuiltOpen: resp.meta.asBuiltOpen } : {}),
        ...(typeof resp?.meta?.asBuiltTotal === 'number' ? { asBuiltTotal: resp.meta.asBuiltTotal } : {}),
        // W53: the project's disciplines for the filter ('' when some sheets have none)
        ...(Array.isArray(resp?.meta?.disciplines) ? { disciplines: resp.meta.disciplines } : {}),
        // W57: sheets per discipline and per type under the current filters (each leaving out its own); zero is absent
        ...(isCounts(resp?.meta?.discipline_counts) ? { disciplineCounts: resp.meta.discipline_counts } : {}),
        ...(isCounts(resp?.meta?.sheet_type_counts) ? { sheetTypeCounts: resp.meta.sheet_type_counts } : {}),
      })),
    );
  }

  /** The as-built record set: every as-built mark on any revision, newest first, with counts per status */
  listAsBuilt(projectId: string, qp: DrawingAsBuiltListParams = {}): Observable<DrawingAsBuiltPage> {
    return this.http.getService(`${this.base(projectId)}/as-built`, qp).pipe(
      map((resp: { data?: DrawingAsBuiltRow[]; meta?: { totalRecords?: number; counts?: Partial<Record<AsBuiltStatus, number>> } }) => ({
        data: resp?.data ?? [],
        totalRecords: resp?.meta?.totalRecords ?? 0,
        counts: { open: 0, reviewed: 0, rejected: 0, incorporated: 0, ...(resp?.meta?.counts ?? {}) },
      })),
    );
  }

  /**
   * Reviews several marks at once, all or nothing. A refusal is a 409 MARKUP_REVIEW_BULK_INVALID whose body lists
   * every mark that could not change (`offenders`); nothing was written.
   */
  bulkReviewAsBuilt(projectId: string, body: { items: string[]; status: AsBuiltStatus; comment?: string | null }): Observable<DrawingAsBuiltRow[]> {
    return this.http.postService(`${this.base(projectId)}/as-built/review`, body).pipe(map((resp: { data?: DrawingAsBuiltRow[] }) => resp?.data ?? []));
  }

  getSheet(projectId: string, sheetUuid: string): Observable<DrawingSheetDetail> {
    return this.http.getService(`${this.base(projectId)}/sheets/${sheetUuid}`).pipe(this.unwrap<DrawingSheetDetail>());
  }

  updateSheet(
    projectId: string,
    sheetUuid: string,
    patch: Partial<Pick<DrawingSheet, 'number' | 'title' | 'discipline' | 'sheet_type' | 'alignment_uuid' | 'station_start' | 'station_end'>>,
  ): Observable<DrawingSheetDetail> {
    return this.http.putService(`${this.base(projectId)}/sheets/${sheetUuid}`, patch).pipe(this.unwrap<DrawingSheetDetail>());
  }

  deleteSheet(projectId: string, sheetUuid: string): Observable<void> {
    return this.http.deleteService(`${this.base(projectId)}/sheets/${sheetUuid}`).pipe(map(() => undefined));
  }

  /**
   * Starts a direct upload. W65: with `sheet_uuid` the set is a new revision of that sheet: the worker binds page 1 to
   * it (match `replaces`), brings the other pages in skipped, and the API names the set "<number> · new revision" when
   * unnamed. The same caller's unfinished upload of the same file resumes with a fresh grant.
   */
  uploadIntent(projectId: string, body: DrawingUploadIntentRequest): Observable<DrawingUploadIntent> {
    return this.http.postService(`${this.base(projectId)}/sets/upload-intent`, body).pipe(this.unwrap<DrawingUploadIntent>());
  }

  /** After the PUT: the API checks the object's size and SHA-256, then queues inspection (202; a repeat is a no-op) */
  finalizeSet(projectId: string, setUuid: string): Observable<DrawingIssueSet> {
    return this.http.postService(`${this.base(projectId)}/sets/${setUuid}/finalize`, {}).pipe(this.unwrap<DrawingIssueSet>());
  }

  /**
   * PUTs the file straight to S3 under the grant, reporting bytes sent. A bare XHR, not HttpClient, so the app's
   * interceptors never add the session token or Sentry headers to a request bound for S3. No answer at all (offline,
   * aborted, blocked) errors as DRAWING_UPLOAD_INTERRUPTED with status 0; an S3 refusal as DRAWING_UPLOAD_FAILED.
   */
  putToS3(upload: DrawingUploadGrant, file: Blob): Observable<DrawingTransferProgress> {
    return new Observable<DrawingTransferProgress>(observer => {
      let url: URL;
      try {
        url = new URL(upload?.url);
      } catch {
        url = null;
      }
      if (!url || url.protocol !== 'https:' || url.username || url.password || upload.method !== 'PUT') {
        observer.error({ status: null, error: { code: 'DRAWING_UPLOAD_FAILED' } });
        return undefined;
      }
      const xhr = this.createXhr();
      let settled = false;
      const interrupted = () => {
        settled = true;
        observer.error({ status: 0, error: { code: DRAWING_UPLOAD_INTERRUPTED } });
      };
      xhr.open('PUT', url.toString());
      xhr.withCredentials = false;
      for (const [header, value] of Object.entries(upload.headers ?? {})) {
        // The browser supplies the length (signed, equal to the file's size); app credentials never go to S3
        if (!['content-length', 'host', 'token', 'authorization', 'cookie'].includes(header.toLowerCase())) {
          xhr.setRequestHeader(header, value);
        }
      }
      xhr.upload.onprogress = (e: ProgressEvent) => {
        observer.next({ loaded: e.loaded, total: e.lengthComputable && e.total ? e.total : file.size });
      };
      xhr.onload = () => {
        settled = true;
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next({ loaded: file.size, total: file.size });
          observer.complete();
        } else if (xhr.status === 0) {
          interrupted();
        } else {
          observer.error({ status: xhr.status, error: { code: 'DRAWING_UPLOAD_FAILED' } });
        }
      };
      xhr.onerror = interrupted;
      xhr.onabort = interrupted;
      xhr.ontimeout = interrupted;
      xhr.send(file);
      return () => {
        if (!settled) {
          settled = true;
          xhr.onabort = null;
          xhr.abort();
        }
      };
    });
  }

  /**
   * Uploads a drawing set PDF without it passing through the API: hash, intent, PUT to S3, finalize. A draft of the
   * same file comes back as is (nothing to upload). Unsubscribing aborts the hash or the PUT; trying again calls the
   * intent again, which resumes the same set.
   */
  uploadSetDirect(
    projectId: string,
    file: File,
    meta: { name?: string; issuedAt?: string | null; sheetUuid?: string | null } = {}
  ): Observable<DrawingUploadEvent> {
    return new Observable<DrawingUploadEvent>(observer => {
      if (file.size > DRAWING_SET_MAX_BYTES) {
        observer.error({ status: 413, error: { code: 'DRAWING_FILE_TOO_LARGE' } });
        return undefined;
      }
      const abort = new AbortController();
      let inner: { unsubscribe(): void } | null = null;
      observer.next({ stage: 'hashing', loaded: 0, total: file.size });
      sha256File(file, { signal: abort.signal, onProgress: (loaded, total) => observer.next({ stage: 'hashing', loaded, total }) }).then(
        sha256 => {
          if (abort.signal.aborted) {
            return;
          }
          const body: DrawingUploadIntentRequest = {
            filename: /\.pdf$/i.test(file.name) ? file.name : `${file.name || 'drawing set'}.pdf`,
            bytes: file.size,
            sha256,
            name: meta.name ?? '',
            issued_at: meta.issuedAt ?? '',
            ...(meta.sheetUuid ? { sheet_uuid: meta.sheetUuid } : {}),
          };
          inner = this.uploadIntent(projectId, body)
            .pipe(
              mergeMap(intent => {
                if (intent?.reused || !intent?.upload) {
                  return from([{ stage: 'done', set: intent?.set, reused: true } as DrawingUploadEvent]);
                }
                return new Observable<DrawingUploadEvent>(sub => {
                  let finalize: { unsubscribe(): void } | null = null;
                  const put = this.putToS3(intent.upload, file).subscribe({
                    next: p => sub.next({ stage: 'uploading', ...p }),
                    error: e => sub.error(e),
                    complete: () => {
                      sub.next({ stage: 'finalizing' });
                      finalize = this.finalizeSet(projectId, intent.set.uuid).subscribe({
                        next: set => sub.next({ stage: 'done', set, reused: false }),
                        error: e => sub.error(e),
                        complete: () => sub.complete(),
                      });
                    },
                  });
                  return () => {
                    put.unsubscribe();
                    finalize?.unsubscribe();
                  };
                });
              })
            )
            .subscribe(observer);
        },
        e => {
          if (!abort.signal.aborted) {
            observer.error({ status: null, error: { code: 'DRAWING_FILE_UNREADABLE' }, cause: e });
          }
        }
      );
      return () => {
        abort.abort();
        inner?.unsubscribe();
      };
    });
  }

  /** The upload's failure copy: an interrupted PUT, or no answer from the API, reads as an interrupted upload */
  uploadFailure(e: unknown): string {
    const code = (e as { error?: { code?: unknown } } | null | undefined)?.error?.code;
    if (code === DRAWING_UPLOAD_INTERRUPTED || this.isOffline(e)) {
      return UPLOAD_INTERRUPTED_COPY;
    }
    return this.requestFailure(e);
  }

  /** The XHR behind putToS3, behind one seam so specs hand in a fake */
  createXhr(): XMLHttpRequest {
    return new XMLHttpRequest();
  }

  listSets(projectId: string, status?: string): Observable<DrawingIssueSet[]> {
    return this.http.getService(`${this.base(projectId)}/sets`, status ? { status } : {}).pipe(map((resp: { data?: DrawingIssueSet[] }) => resp?.data ?? []));
  }

  getSet(projectId: string, setUuid: string): Observable<DrawingIssueSet> {
    return this.http.getService(`${this.base(projectId)}/sets/${setUuid}`).pipe(this.unwrap<DrawingIssueSet>());
  }

  updateSetRevision(projectId: string, setUuid: string, revisionUuid: string, patch: Record<string, unknown>): Observable<DrawingSetRevision | null> {
    return this.http
      .putService(`${this.base(projectId)}/sets/${setUuid}/revisions/${revisionUuid}`, patch)
      .pipe(this.unwrap<DrawingSetRevision | null>());
  }

  /** `acknowledge`: the pages a 409 DRAWING_SET_NEEDS_ACKNOWLEDGEMENT listed, published anyway */
  publishSet(projectId: string, setUuid: string, acknowledge?: string[]): Observable<DrawingIssueSet> {
    const body = acknowledge?.length ? { acknowledge_warnings: acknowledge } : {};
    return this.http.postService(`${this.base(projectId)}/sets/${setUuid}/publish`, body).pipe(this.unwrap<DrawingIssueSet>());
  }

  /** Discard set: a set never published goes, with its pages */
  discardSet(projectId: string, setUuid: string): Observable<void> {
    return this.http.deleteService(`${this.base(projectId)}/sets/${setUuid}`).pipe(map(() => undefined));
  }

  withdrawSet(projectId: string, setUuid: string): Observable<DrawingIssueSet> {
    return this.http.postService(`${this.base(projectId)}/sets/${setUuid}/withdraw`, {}).pipe(this.unwrap<DrawingIssueSet>());
  }

  withdrawRevision(projectId: string, revisionUuid: string): Observable<DrawingSetRevision> {
    return this.http.postService(`${this.base(projectId)}/revisions/${revisionUuid}/withdraw`, {}).pipe(this.unwrap<DrawingSetRevision>());
  }

  getRevision(projectId: string, revisionUuid: string): Observable<DrawingRevisionDetail> {
    return this.http.getService(`${this.base(projectId)}/revisions/${revisionUuid}`).pipe(this.unwrap<DrawingRevisionDetail>());
  }

  /** "Linked here": records marked on one revision, newest first, with per-day counts over every page. */
  listLinked(projectId: string, revisionUuid: string, qp: { start?: number; total?: number } = {}): Observable<DrawingLinkedPage> {
    return this.http.getService(`${this.base(projectId)}/revisions/${revisionUuid}/linked`, qp).pipe(
      map((resp: { data?: DrawingLinkedRecord[]; meta?: { totalRecords?: number; days?: DrawingLinkedPage['days'] } }) => ({
        data: resp?.data ?? [],
        totalRecords: resp?.meta?.totalRecords ?? 0,
        days: resp?.meta?.days ?? [],
      })),
    );
  }

  /** Shared redlines on the sheet's superseded revision still waiting for a decision. */
  carryForward(projectId: string, sheetUuid: string): Observable<DrawingCarryForward> {
    return this.http.getService(`${this.base(projectId)}/sheets/${sheetUuid}/carry-forward`).pipe(this.unwrap<DrawingCarryForward>());
  }

  /** Keep / reposition / retire; answers with the offers still open. A repeat for a decided redline is a no-op. */
  applyCarryForward(projectId: string, sheetUuid: string, items: DrawingCarryDecision[]): Observable<DrawingCarryForward> {
    return this.http.postService(`${this.base(projectId)}/sheets/${sheetUuid}/carry-forward`, { items }).pipe(this.unwrap<DrawingCarryForward>());
  }

  /** Change notes on an issue set ('' clears). */
  updateSet(projectId: string, setUuid: string, notes: string): Observable<DrawingIssueSet> {
    return this.http.putService(`${this.base(projectId)}/sets/${setUuid}`, { notes }).pipe(this.unwrap<DrawingIssueSet>());
  }

  /**
   * Queues a zip of every current sheet (PDF with shared redlines) plus index.csv; the as-built record set adds
   * deviations.csv. One running export per kind; the requester's running one comes back as is.
   */
  requestExport(projectId: string, kind: DrawingExportKind = 'current'): Observable<{ uuid: string; status: string }> {
    return this.http.postService(`${this.base(projectId)}/export`, { kind }).pipe(this.unwrap<{ uuid: string; status: string }>());
  }

  exportStatus(projectId: string, exportUuid: string): Observable<DrawingExportJob> {
    return this.http.getService(`${this.base(projectId)}/export/${exportUuid}`).pipe(this.unwrap<DrawingExportJob>());
  }

  /** Places the revision on the map: a `placed_plan` source version the map import worker converts (202). */
  place(projectId: string, revisionUuid: string, body: DrawingPlaceRequest): Observable<DrawingPlaceResult> {
    return this.http.postService(`${this.base(projectId)}/revisions/${revisionUuid}/place`, body).pipe(this.unwrap<DrawingPlaceResult>());
  }

  placement(projectId: string, revisionUuid: string): Observable<DrawingPlacement> {
    return this.http.getService(`${this.base(projectId)}/revisions/${revisionUuid}/placement`).pipe(this.unwrap<DrawingPlacement>());
  }

  /** R50: discards the placement waiting for review; answers the placement as it now reads (publishers only) */
  discardPlacement(projectId: string, revisionUuid: string): Observable<DrawingPlacement & { discardedVersionUuid: string }> {
    return this.http
      .deleteService(`${this.base(projectId)}/revisions/${revisionUuid}/placement`)
      .pipe(this.unwrap<DrawingPlacement & { discardedVersionUuid: string }>());
  }

  /**
   * Remove from map (api 3692cccc): unpublishes the sheet's placed layer (records keep their sheet locations). With the
   * catalog revision the caller read, a map changed since is a 409 MAP_CATALOG_CONFLICT rather than a blind removal.
   */
  removePlacement(projectId: string, revisionUuid: string, expectedCatalogRevision: number | null):
    Observable<DrawingPlacement & { removedLayerId: string; catalogRevision: number }> {
    const query = Number.isInteger(expectedCatalogRevision) ? `?expectedCatalogRevision=${expectedCatalogRevision}` : '';
    return this.http
      .deleteService(`${this.base(projectId)}/revisions/${revisionUuid}/placement/published${query}`)
      .pipe(this.unwrap<DrawingPlacement & { removedLayerId: string; catalogRevision: number }>());
  }

  /** Remove from map's refusals: a placement in review reads differently here than for placing (the shared copy) */
  removeFailure(e: { error?: { code?: unknown } } | null): string {
    return e?.error?.code === 'PLACEMENT_IN_PROGRESS' ? 'Finish or discard the placement in review first.' : this.requestFailure(e);
  }

  /** Copies a GeoPDF map layer's original into a draft issue set, which opens in the set review. */
  registerLayer(projectId: string, sourceUuid: string): Observable<DrawingIssueSet> {
    return this.http.postService(`${this.base(projectId)}/register-layer`, { sourceUuid }).pipe(this.unwrap<DrawingIssueSet>());
  }

  /** Pulls the human message from an HttpErrorResponse: `error.error` (mapResponse) or `error.message` (markup 409). */
  errorMessage(e: unknown): string | undefined {
    const body = (e as { error?: { error?: unknown; message?: unknown } } | null | undefined)?.error;
    for (const candidate of [body?.error, body?.message]) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
    return undefined;
  }

  /** No response reached the app (status 0: offline, the API down or restarting, CORS) */
  isOffline(e: unknown): boolean {
    return (e as { status?: unknown } | null | undefined)?.status === 0;
  }

  /**
   * What a failed request says: the connection wording when nothing answered (`save` for a write), otherwise the
   * error code's copy (with the server text for parameterised codes).
   */
  requestFailure(e: unknown, opts: { save?: boolean } = {}): string {
    if (this.isOffline(e)) {
      return opts.save ? OFFLINE_SAVE_COPY : OFFLINE_COPY;
    }
    const code = (e as { error?: { code?: unknown } } | null | undefined)?.error?.code;
    return this.failureMessage(typeof code === 'string' ? code : undefined, this.errorMessage(e));
  }

  /** The server text names the sheet, label or page, so it wins for the codes that carry parameters. */
  failureMessage(code: string | undefined, serverMessage?: unknown): string {
    if (code && SERVER_MESSAGE_CODES.has(code) && typeof serverMessage === 'string' && serverMessage.trim()) {
      return serverMessage;
    }
    return (code && COPY[code]) || FALLBACK_COPY;
  }
}
